import React, { useCallback, useEffect, useState } from "react";
import { IAnalysisSummaryResponse, IWhoPays } from "../../../../interface/analysisSummary";
import { useSelector } from "react-redux";
import { IREIGraphDataSet } from "../../../../interface/graph";
import Graph from "../../../../components/ui/graph";
import {
  formatCurrencyWithDecimal,
  formatNumberWithDecimal,
  sortObjectByNumericKeys,
  whoPaysColors,
} from "../../../../utils/commonUtil";
import { YEARS } from "../../../../utils/constants";

const WhoPays: React.FC = () => {
  const analysisData: IAnalysisSummaryResponse = useSelector((state: any) => state.analyser.analysisSummary);
  const [chartData, setChartData] = useState([] as IREIGraphDataSet[]);
  const [chartOptions, setChartOptions] = useState({});
  const whoPaysData = analysisData?.WhoPays;

  const extractData = (
    dataObject: IWhoPays["UpdatedTaxman"] | IWhoPays["Tenants"] | IWhoPays["You"],
    objectName: "UpdatedTaxman" | "Tenants" | "You"
  ) => {
    const sortedData = sortObjectByNumericKeys(dataObject);

    const data = Object?.entries(sortedData)
      .map(([key, value]) => {
        if (objectName == "You") {
          let youContribution = Number(formatNumberWithDecimal(value as number, 2, 2).replace(",", "")) * -1;
          youContribution = youContribution < 0 ? 0 : youContribution;
          return youContribution;
        } else {
          return Number(formatNumberWithDecimal(Math.abs(value as number), 2, 2).replace(",", ""));
        }
      })
      .slice(1, 11);

    return data;
  };

  const formatYAxisTick = useCallback((value: number) => {
    const num = Math.round(Number(value));
    const formatted = "$" + num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    return formatted;
  }, []);

  useEffect(() => {
    if (!whoPaysData?.UpdatedTaxman || !whoPaysData?.Tenants || !whoPaysData?.You) {
      setChartData([]);
      return;
    }

    const taxManData = extractData(whoPaysData.UpdatedTaxman, "UpdatedTaxman");
    const tenantsData = extractData(whoPaysData.Tenants, "Tenants");
    const youData = extractData(whoPaysData.You, "You");

    const TooltipData: number[] = taxManData.map(
      (num, index) => (tenantsData[index] || 0) + (youData[index] || 0) + (taxManData[index] || 0)
    );

    const data = [
      {
        type: "bar",
        label: "Tenants",
        extraData: TooltipData,
        data: tenantsData,
        backgroundColor: [whoPaysColors.tenants],
      },
      {
        type: "bar",
        label: "Taxman",
        extraData: TooltipData,
        data: taxManData,
        backgroundColor: [whoPaysColors.taxman],
      },
      {
        type: "bar",
        label: "You",
        extraData: TooltipData,
        data: youData,
        backgroundColor: [whoPaysColors.you],
      },
    ];

    const options = {
      maintainAspectRatio: true,
      aspectRatio: 3.8,
      plugins: {
        legend: {
          display: true,
          position: "right",
          reverse: true,
        },
        tooltip: {
          title: "",
          callbacks: {
            label: (tooltipItem: any) => {
              const correspondingValue = YEARS.find(
                (key) => key.label === tooltipItem.label.replace("yr", "Year ")
              )?.label;

              const totalPay = `Total ${correspondingValue}: ${formatCurrencyWithDecimal(
                tooltipItem.dataset.extraData[tooltipItem.dataIndex]
              )}`;

              const variablePay = `${tooltipItem.dataset.label} pays: ${formatCurrencyWithDecimal(
                tooltipItem.dataset.data[tooltipItem.dataIndex]
              )}`;

              return [variablePay, totalPay];
            },
          },
          multiLine: true,
        },
      },
      scales: {
        y: {
          height: 6000,
          stacked: true,
          stackWeight: 1,
          ticks: {
            callback: formatYAxisTick,
          },
          grid: {
            display: false,
            drawBorder: false,
          },
          beginAtZero: true,
        },
        x: {
          stacked: true,
          grid: {
            display: false,
            drawBorder: false,
          },
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [analysisData]);

  return (
    <Graph
      id="wp-graph-whopays"
      customClass="h-full"
      dataSet={chartData}
      customOptions={chartOptions}
      graphType="bar"
      labels={["yr1", "yr2", "yr3", "yr4", "yr5", "yr6", "yr7", "yr8", "yr9", "yr10"]}
    ></Graph>
  );
};

export default WhoPays;
